import { useState } from "react";
import { Form, Link, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import AutoComplete from "src/components/common/autoComplete";
import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import {
    addDate,
    pipelineOptions,
    regionOptions,
    subPipelineOptions,
} from "src/components/common/functions";
import { LatLngTuple } from "leaflet";
import { defaultProject, Project } from "./types";

export default function ProjectForm({ keyword }: { keyword: "Add" | "Edit" }) {
    const project = (useRouteLoaderData("project") as Project | undefined) ?? defaultProject;
    const [searchParams] = useSearchParams();
    const [formValues, setFormValues] = useState<Omit<
		Project,
		| "id"
		| "location"
		| "coords"
		| "projectCreationDate"
		| "projectDateUpdated"
		| "projectCreatorName"
		| "projectUpdaterName"
		| "projectCreatorId"
		| "projectUpdaterId"
		| "statusChanger"
		| "statusChangerId"
		| "statusChangeDateTime"
		| "projectFiles"
		| "folderId"
		| "budget"
		| "reportLink"
		| "reportCreationDate"
		| "cctv"
		| "KML"
		| "rate"
		| "profit"
		| "requestorId"
		| "requestor"
	>>(
    {
        usaTicketDate: project.usaTicketDate ?? "",
        projectNumber: project.projectNumber ?? "",
        description: project.description ?? "",
        notes: project.notes ?? "",
        pipeline: project.pipeline ?? "",
        subPipeline: project.subPipeline ?? "",
        stage: project.stage ?? "",
        laborHours: project.laborHours ?? "",
        materialCost: project.materialCost ?? "",
        billingType: project.billingType ?? "",
        days: project.days ?? "",
        organizationId: project.organizationId ?? "",
        organizationName: project.organizationName ?? "",
        reportNeeded: project.reportNeeded ?? "",
        prevailing: project.prevailing,
        cpr: project.cpr,
        budgets: project.budgets,
        opportunityId: project.opportunityId ?? searchParams.get("opportunityId") ?? "",
        opportunityName: project.opportunityName ?? searchParams.get("opportunityName") ?? "",
        proposalId: project.proposalId ?? searchParams.get("proposalId") ?? "",
        proposalNumber: project.proposalNumber ?? searchParams.get("proposalNumber") ?? "",
        projectManagerId: project.projectManagerId ?? null, 
        projectManagerName: project.projectManagerName ?? null, 
        contacts: project.contacts ?? [],
        region: project.region ?? "",
        clientPONumber: project.clientPONumber ?? "",
        usaTicketNumber: project.usaTicketNumber ?? "",
        nearestMedicalFacility: project.nearestMedicalFacility ?? "",
        clientProjectNumber: project.clientProjectNumber ?? "",
        dirNumber: project.dirNumber ?? "",
        clientSignatureRequired: project.clientSignatureRequired,
        accurateTime: project.accurateTime,
        sendToClient: project.sendToClient,
        reportTechName: project.reportTechName ?? "", 
        reportTechId: project.reportTechId ?? ""
    });
    const subPipelines = subPipelineOptions[formValues.pipeline as keyof typeof subPipelineOptions] ?? [];
    const stages = pipelineOptions[formValues.pipeline as keyof typeof pipelineOptions] ?? [];
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormValues({ ...formValues, [name]: type === "checkbox" ? checked : value}); 
    };

    return (
        <Form
            method="post"
            encType="multipart/form-data"
        >
            <input type="hidden" name="action" value={keyword} />
            <input type="hidden" name="id" value={project.id || ""} />
            <input type="hidden" name="folderId" value={project.folderId || ""} />
            <div className="cssSeperateWhenWide2 ps-3 mb-2">
                <div className="flex-grow-1 maxWidth-50 me-3">
                    <h4 className="my-2">Project Detail</h4>
                    <div className="rounded border mb-2 p-2">
                        <div className="d-flex align-items-center">
                            <label htmlFor="region" className="text-nowrap required-field fw-bold">
                                Region:
                            </label>
                            <select
                                id="region"
                                required
                                className = "form-control flex-grow-1 ms-3"
                                name = "region"
                                value = {formValues.region}
                                onChange = {handleChange}
                            >
                                <option value="" disabled>Select</option>
                                {regionOptions.map(option => (
                                    <option key={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="pipeline" className="text-nowrap required-field fw-bold">
                                Pipeline:
                            </label>
                            <select
                                id = "pipeline"
                                required
                                className = "form-control flex-grow-1 ms-3"
                                name = "pipeline"
                                value = {formValues.pipeline}
                                onChange = {event => {
                                    const value = event.currentTarget.value;
                                    setFormValues({
                                        ...formValues,
                                        pipeline: value,
                                        stages: pipelineOptions[value as keyof typeof pipelineOptions] ?? [],
                                        stage: "",
                                        subPipeline: "",
                                    });
                                }}
                            >
                                <option value = "">Select Pipeline</option>
                                {Object.keys(pipelineOptions).map(value => (
                                    <option key = {value}>{value}</option>
                                ))}
                            </select>
                        </div>
                        {subPipelines.length ? (
                            <div className = "d-flex align-items-center mt-2">
                                <label htmlFor = "subPipeline" className="text-nowrap fw-bold">
                                    Sub Pipeline:
                                </label>
                                <select
                                    id = "subPipeline"
                                    className = "form-control flex-grow-1 ms-3"
                                    name = "subPipeline"
                                    value = {formValues.subPipeline}
                                    onChange = {handleChange}
                                >
                                    <option value = "">Select Sub Pipeline</option>
                                    {subPipelines.map(value => (
                                        <option key = {value}>{value}</option>
                                    ))}
                                </select>
                            </div>
                        ) : null}
                        <div className = "d-flex align-items-center mt-2">
                            <label htmlFor = "reportNeeded" className="text-nowrap required-field fw-bold">
                                Report Needed:
                            </label>
                            <select
                                id = "reportNeeded"
                                required
                                className = "form-control flex-grow-1 ms-3"
                                name = "reportNeeded"
                                value = {formValues.reportNeeded}
                                onChange = {handleChange}
                            >
                                <option value = "">Select</option>
                                <option value = "no">No</option>
                                <option value = "yes">Yes</option>
                            </select>
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="stage" className="text-nowrap required-field fw-bold">
                                Stage:
                            </label>
                            <select
                                id="stage"
                                required
                                className="form-control flex-grow-1 ms-3"
                                name="stage"
                                value={formValues.stage}
                                onChange={handleChange}
                            >
                                <option value="">Select Stage</option>
                                {stages
                                    .filter(stage => (formValues.reportNeeded !== "yes" ? stage !== "Report Deliverable" : true))
                                    .map(stage => (
                                        <option key={stage}>{stage}</option>
                                    ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="projectNumber" className="text-nowrap required-field fw-bold">
                                Project Number:
                            </label>
                            <input
                                id="projectNumber"
                                type="text"
                                className="form-control flex-grow-1 ms-3"
                                name="projectNumber"
                                placeholder="Project Number"
                                required
                                readOnly={formValues.projectNumber === null}
                                value={formValues.projectNumber ?? ""}
                                onChange={handleChange}
                            />
                            <label htmlFor="projectNumberPending" className="text-nowrap ms-3">
                                Pending
                            </label>
                            <input
                                id="projectNumberPending"
                                type="checkbox"
                                value = "yes"
                                className="cssCheckBox ms-2"
                                checked = {formValues.projectNumber === null}
                                onChange={e => {
                                    if(e.currentTarget.checked) setFormValues({ ...formValues, projectNumber: null});
                                    else setFormValues({ ...formValues, projectNumber: "" });
                                }}
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label className="required-field fw-bold">Organization:</label>
                            <div className="flex-grow-1 ms-3">
                                <AsyncFormSelect
                                    className="flex-grow-1"
                                    placeholder="Search organization..."
                                    required={true}
                                    name="organizationId"
                                    inputId="organizationId"
                                    fileName="common/searchOrganization.php"
                                    value={formValues.organizationId ? { label: formValues.organizationName, value: formValues.organizationId } : null}
                                    onChange={next =>
                                        setFormValues({
                                            ...formValues,
                                            organizationId: (next as SelectOption)?.value ?? "",
                                            organizationName: (next as SelectOption)?.label ?? "",
                                            contacts: [], // Clear contacts when organization changes
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label className="required-field fw-bold">Contacts:</label>
                            <div className="flex-grow-1 ms-3">
                                <AsyncFormSelect
                                    className="flex-grow-1 ms-3"
                                    placeholder = "Search contacts…"
                                    required = {true}
                                    name = {"contactIds"}
                                    inputId = {"contactIds"}
                                    fileName = "common/searchContactInOrganization.php"
                                    value = {formValues.contacts}
                                    isMulti = {true}
                                    onChange = {next => 
                                        setFormValues({
                                            ...formValues,
                                            contacts: next as SelectOption[]
                                        })
                                    }
                                    formValues={{ organizationId: formValues.organizationId ?? "" }}
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="clientProjectNumber" className="text-nowrap required-field fw-bold">
                                Client Project Name / Job Number / PM Number:
                            </label>
                            <input
                                id="clientProjectNumber"
                                type="text"
                                required
                                className="form-control flex-grow-1 ms-3"
                                name="clientProjectNumber"
                                placeholder="Client Project Name / Job Number / PM Number"
                                value={formValues.clientProjectNumber}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="projectManagerId" className="text-nowrap required-field fw-bold">
                                Project Manager:
                            </label>
                            <AsyncFormSelect
                                className="flex-grow-1 ms-3"
                                placeholder = "Search users…"
                                required = {true}
                                name = {"projectManagerId"}
                                inputId = {"projectManagerId"}
                                fileName = "common/searchUser.php"
                                value = {formValues.projectManagerId ? {label: formValues.projectManagerName!, value: String(formValues.projectManagerId ?? "")} : null}
                                onChange = {next => 
                                    setFormValues({
                                        ...formValues,
                                        projectManagerId: !next ? null : ((next as SelectOption).value),
                                        projectManagerName: (next as SelectOption)?.label ?? null
                                    })
                                }
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="clientPONumber" className="text-nowrap fw-bold">
                                Client PO:
                            </label>
                            <input
                                id="clientPONumber"
                                type="text"
                                className="form-control flex-grow-1 ms-3"
                                name="clientPONumber"
                                placeholder="Client PO"
                                value={formValues.clientPONumber}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="usaTicketNumber" className="text-nowrap fw-bold">
                                USA Ticket Number:
                            </label>
                            <input
                                id="usaTicketNumber"
                                type="text"
                                className="form-control flex-grow-1 ms-3"
                                name="usaTicketNumber"
                                placeholder="USA Ticket Number"
                                value={formValues.usaTicketNumber}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="usaTicketDate" className="text-nowrap fw-bold">
                                USA Ticket Date:
                            </label>
                            <input
                                id="usaTicketDate"
                                type="date"
                                min="1900-01-01"
                                max="2999-12-31"
                                className="form-control flex-grow-1 ms-3"
                                name="usaTicketDate"
                                value={formValues.usaTicketDate}
                                onChange={handleChange}
                            />
                        </div>
                        <div className = "d-flex align-items-center mt-2">
                            <label className = "text-nowrap fw-bold">USA Ticket Expiration Date:</label>
                            <input
                                readOnly
                                type = "date"
                                min = "1900-01-01"
                                max = "2999-12-31"
                                className = "form-control flex-grow-1 ms-3"
                                value = {addDate(formValues.usaTicketDate, 28)}
                            />
                        </div>
                        <div className = "d-flex align-items-center mt-2">
                            <label className = "text-nowrap fw-bold">Job Site Location:</label>
                            <AutoComplete
                                className = "ms-3"
                                location  ={formValues.location}
                                coords = {formValues.coords ? formValues.coords.split(",").map(Number) as LatLngTuple : undefined}
                                locationName = "location"
                                coordsName = "coords"
                                id = {""}
                            />
                        </div>
                        <div className = "d-flex align-items-center mt-2">
                            <label htmlFor = "nearestMedicalFacility" className = "text-nowrap fw-bold">
                                Location of Nearest Medical Facility:
                            </label>
                            <input
                                id = "nearestMedicalFacility"
                                type = "text"
                                className = "form-control flex-grow-1 ms-3"
                                name = "nearestMedicalFacility"
                                value = {formValues.nearestMedicalFacility}
                                onChange = {handleChange}
                            />
                        </div>
                        <div className = "d-flex align-items-center mt-2">
                            <label htmlFor = "billingType" className="text-nowrap fw-bold">
                                Billing Type:
                            </label>
                            <select
                                id = "billingType"
                                className = "form-control flex-grow-1 ms-3"
                                name = "billingType"
                                value = {formValues.billingType}
                                onChange = {handleChange}
                            >
                                <option value="">Select</option>
                                <option>Day Rate</option>
                                <option>T &amp; M</option>
                            </select>
                        </div>
                        {formValues.billingType === "Day Rate" ? (
                            <div className="d-flex align-items-center mt-2">
                                <label htmlFor="days" className="text-nowrap fw-bold">
                                    Estimate Days:
                                </label>
                                <input
                                    id = "days"
                                    type = "number"
                                    step = "any"
                                    className = "form-control flex-grow-1 ms-3"
                                    name = "days"
                                    placeholder = "Days"
                                    value = {formValues.days}
                                    onChange = {handleChange}
                                />
                            </div>
                        ) : null}
                        {formValues.billingType === "T & M" ? (
                            <>
                                <div className="d-flex align-items-center mt-2">
                                    <label htmlFor="laborHours" className="text-nowrap fw-bold">
                                        Estimate Labor Hours:
                                    </label>
                                    <input
                                        id="laborHours"
                                        type="number"
                                        step="any"
                                        className="form-control flex-grow-1 ms-3"
                                        name="laborHours"
                                        placeholder="Labor Hours"
                                        value={formValues.laborHours}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="d-flex align-items-center mt-2">
                                    <label htmlFor="materialCost" className="text-nowrap fw-bold">
                                        Estimate Material Cost:
                                    </label>
                                    <input
                                        id="materialCost"
                                        type="number"
                                        step="any"
                                        className="form-control flex-grow-1 ms-3"
                                        name="materialCost"
                                        placeholder="Material Cost"
                                        value={formValues.materialCost}
                                        onChange={handleChange}
                                    />
                                </div>
                            </>
                        ) : null}
                        <input type="hidden" name="budgets" value={JSON.stringify(formValues.budgets)} readOnly />
                        <label className="text-nowrap fw-bold mt-2">Detail Budget:</label>
                        {formValues.budgets.map((value: any, index: number) => (
                            <div
                                key={index}
                                className="cssSeperateWhenWide border rounded ps-2 mt-2 align-items-center overflow-auto"
                            >
                                <div className="me-2 my-2">
                                    <label htmlFor={`amount-${index}`} className="text-nowrap">Amount ($):</label>
                                    <input
                                        id={`amount-${index}`}
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        value={value.amount}
                                        onChange={event =>
                                            setFormValues({
                                                ...formValues,
                                                budgets: [
                                                    ...formValues.budgets.slice(0, index),
                                                    { ...value, amount: event.currentTarget.value },
                                                    ...formValues.budgets.slice(index + 1),
                                                ],
                                            })
                                        }
                                    />
                                </div>
                                <div className="me-2 my-2">
                                    <label htmlFor={`start-${index}`}>Start:</label>
                                    <input
                                        id={`start-${index}`}
                                        type="date"
                                        required={Boolean(value.amount)}
                                        className="form-control"
                                        disabled={index > 0 && !formValues.budgets[index - 1]?.endUnknown}
                                        value={index > 0 && !formValues.budgets[index - 1]?.endUnknown ? formValues.budgets[index - 1]?.end : value.start}
                                        onChange={event =>
                                            setFormValues({
                                                ...formValues,
                                                budgets: [
                                                    ...formValues.budgets.slice(0, index),
                                                    { ...value, start: event.currentTarget.value },
                                                    ...formValues.budgets.slice(index + 1),
                                                ],
                                            })
                                        }
                                    />
                                </div>
                                <div className="me-2 my-2">
                                    <label htmlFor={`end-${index}`}>End:</label>
                                    <input
                                        id={`end-${index}`}
                                        type="date"
                                        required={Boolean(value.amount)}
                                        disabled={value.endUnknown}
                                        className="form-control"
                                        min={index > 0 ? formValues.budgets[index - 1]?.end : value.start}
                                        value={value.end}
                                        onChange={event =>
                                            setFormValues({
                                                ...formValues,
                                                budgets: [
                                                    ...formValues.budgets.slice(0, index),
                                                    { ...value, end: event.currentTarget.value },
                                                    ...formValues.budgets.slice(index + 1),
                                                ],
                                            })
                                        }
                                    />
                                </div>
                                <div className="me-2 my-2">
                                    <label htmlFor={`end-unknown-${index}`} className="text-nowrap">
                                        End Unknown
                                    </label>
                                    <input
                                        id={`end-unknown-${index}`}
                                        type="checkbox"
                                        value = "yes"
                                        className="cssCheckBox d-block"
                                        disabled={index < formValues.budgets.length - 1}
                                        checked={Boolean(value.endUnknown)}
                                        onChange={event =>
                                            setFormValues({
                                                ...formValues,
                                                budgets: [
                                                    ...formValues.budgets.slice(0, index),
                                                    { ...value, endUnknown: event.currentTarget.checked, end: "" },
                                                    ...formValues.budgets.slice(index + 1),
                                                ],
                                            })
                                        }
                                    />
                                </div>
                                {index !== 0 ? (
                                    <div className="ms-2 me-2">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() => {
                                                if (!window.confirm("Are you sure to delete this row?")) return;
                                                setFormValues({
                                                    ...formValues,
                                                    budgets: [
                                                        ...formValues.budgets.slice(0, index),
                                                        ...formValues.budgets.slice(index + 1),
                                                    ],
                                                });
                                            }}
                                        >
                                            x
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                        <div className="mt-2">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                    setFormValues({ ...formValues,
                                        budgets: [
                                            ...formValues.budgets.map((item: any) => ({ ...item, endUnknown: false })),
                                            { amount: "", start: "", end: "" },
                                        ],
                                    })
                                }
                            >
                                Add Change Order
                            </button>
                        </div>
                        <div>
                            <div className="fw-bold mt-2">Description:</div>
                            <div className="mt-2">
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={{
                                        toolbar: ["selectAll", "undo", "redo", "bold", "italic", "numberedList", "bulletedList"],
                                    }}
                                    data={formValues.description}
                                    onChange={(_: any, editor: { getData: () => string }) =>
                                        setFormValues({ ...formValues, description: editor.getData() })
                                    }
                                />
                            </div>
                            <input type="hidden" name="description" value={formValues.description} readOnly />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="opportunityId" className="fw-bold">
                                Opportunity:
                            </label>
                            <div className="flex-grow-1 ms-3">
                                <AsyncFormSelect
                                    className="flex-grow-1"
                                    placeholder="Search opportunity..."
                                    name="opportunityId"
                                    inputId="opportunityId"
                                    fileName="common/searchOpportunity.php"
                                    value={formValues.opportunityId ? { label: formValues.opportunityName, value: formValues.opportunityId } : null}
                                    onChange={next =>
                                        setFormValues({
                                            ...formValues,
                                            opportunityId: (next as SelectOption)?.value ?? "",
                                            opportunityName: (next as SelectOption)?.label ?? ""
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <label htmlFor="proposalId" className="fw-bold">
                                Proposal:
                            </label>
                            <div className="flex-grow-1 ms-3">
                                <AsyncFormSelect
                                    className="flex-grow-1"
                                    placeholder="Search proposal..."
                                    name="proposalId"
                                    inputId="proposalId"
                                    fileName="common/searchProposal.php"
                                    value={formValues.proposalId ? { label: formValues.proposalNumber, value: formValues.proposalId } : null}
                                    onChange={next =>
                                        setFormValues({
                                            ...formValues,
                                            proposalId: (next as SelectOption)?.value ?? "",
                                            proposalNumber: (next as SelectOption)?.label ?? ""
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex-grow-1 maxWidth-50">
                    <h4 className="my-2 ms-2">Compliance</h4>
                    <div className="rounded border p-2 ms-2">
                        <input
                            type="checkbox"
                            value = "yes"
                            className="cssCheckBox"
                            id="prevailing"
                            name="prevailing"
                            checked={formValues.prevailing === "yes"}
                            onChange={e => {
                                if (e.currentTarget.checked) setFormValues({ ...formValues, prevailing: "yes" });
                                else setFormValues({ ...formValues, prevailing: "no" });
                            }}
                        />
                        <label className="ms-2" htmlFor="prevailing">
                            Prevailing Job
                        </label>
                        {formValues.prevailing === "yes" ? (
                            <div className="mt-2">
                                <input
                                    type="checkbox"
                                    value = "yes"
                                    className="cssCheckBox"
                                    id="cpr"
                                    name="cpr"
                                    checked={formValues.cpr === "yes"}
                                    onChange={e => {
                                        if (e.currentTarget.checked) setFormValues({ ...formValues, cpr: "yes" });
                                        else setFormValues({ ...formValues, cpr: "no" });
                                    }}
                                />
                                <label className="ms-2" htmlFor="cpr">
                                    Certified Payroll Needed
                                </label>
                            </div>
                        ) : null}
                        {formValues.prevailing === "yes" && formValues.cpr === "yes" ? (
                            <div className="mt-2">
                                <div className="d-flex align-items-center">
                                    <label htmlFor="dirNumber">DIR Number:</label>
                                    <input
                                        id="dirNumber"
                                        type="text"
                                        className="form-control flex-grow-1 ms-3"
                                        name="dirNumber"
                                        value = {formValues.dirNumber}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="mt-2">
                                    <a
                                        href="https://www.dir.ca.gov/DAS/DASForm140.pdf"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        DAS 140 Form
                                    </a>{" "}
                                    must be emailed before the job started.
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <h4 className="my-2 ms-2">Others</h4>
                    <div className="border rounded p-2 ms-2">
                        <div>
                            <input
                                type="checkbox"
                                value = "yes"
                                className="cssCheckBox"
                                id="accurateTime"
                                name="accurateTime"
                                checked = {formValues.accurateTime === "yes"}
                                onChange={e => {
                                    if (e.currentTarget.checked) setFormValues({ ...formValues, accurateTime: "yes" });
                                    else setFormValues({ ...formValues, accurateTime: "no" });
                                }}
                            />
                            <label className="ms-2" htmlFor="accurateTime">
                                Accurate Time on Job Tag
                            </label>
                        </div>
                        {formValues.pipeline !== "GPR Concrete Scanning and Coring Project" ? (
                            <>
                                <div className="mt-2">
                                    <input
                                        type="checkbox"
                                        value = "yes"
                                        className="cssCheckBox"
                                        id="clientSignatureRequired"
                                        name="clientSignatureRequired"
                                        checked={formValues.clientSignatureRequired === "yes"}
                                        onChange={e => {
                                            if (e.currentTarget.checked) setFormValues({ ...formValues, clientSignatureRequired: "yes" });
                                            else setFormValues({ ...formValues, clientSignatureRequired: "no" });
                                        }}
                                    />
                                    <label className="ms-2" htmlFor="clientSignatureRequired">
                                        Client Signature Required
                                    </label>
                                </div>
                                <div className="mt-2">
                                    <input
                                        type="checkbox"
                                        value = "yes"
                                        className="cssCheckBox"
                                        id="sendToClient"
                                        name="sendToClient"
                                        checked={formValues.sendToClient === "yes"}
                                        onChange={e => {
                                            if (e.currentTarget.checked) setFormValues({ ...formValues, sendToClient: "yes" });
                                            else setFormValues({ ...formValues, sendToClient: "no" });
                                        }}
                                    />
                                    <label className="ms-2" htmlFor="sendToClient">
                                        Send To Client
                                    </label>
                                </div>
                            </>
                        ) : null}
                    </div>
                    <h4 className="my-2 ms-2">Notes</h4>
                    <div className="mt-2 ms-2">
                        <CKEditor
                            editor={ClassicEditor}
                            config={{
                                toolbar: ["selectAll", "undo", "redo", "bold", "italic", "numberedList", "bulletedList"],
                            }}
                            data={formValues.notes}
                            onChange={(_: any, editor: { getData: () => string }) =>
                                setFormValues({ ...formValues, notes: editor.getData() })
                            }
                        />
                        <input type="hidden" name="notes" value={formValues.notes} readOnly />
                    </div>
                </div>
            </div>
            <div className="px-3 pb-3 d-flex justify-content-between">
                <button type="submit" className="btn btn-primary">
                    {keyword}
                </button>
                <Link to=".." replace className="btn btn-outline-secondary">
                    Back
                </Link>
            </div>
        </Form>
    );
}

/*
{formValues.reportNeeded === "yes" ? (
    <>
        {keyword === "Add" ? (
            <div className="d-flex align-items-center mt-2">
                <label htmlFor="reportTechId" className="text-nowrap required-field fw-bold">
                    Report Tech:
                </label>
                <AsyncFormSelect
                    className = "ms-2 flex-grow-1"
                    placeholder = "Search user..."
                    required = {true}
                    name = "reportTechId"
                    inputId = "reportTechId"
                    fileName = "common/searchUser.php"
                    value = {formValues.reportTechId ? { label: formValues.reportTechName, value: formValues.reportTechId } : null}
                    onChange={next =>
                        setFormValues(prev => ({
                            ...prev,
                            reportTechId: (next as SelectOption)?.value ?? "",
                            reportTechName: (next as SelectOption)?.label ?? "",
                        }))
                    }
                />
            </div>
        ) : (
            <div className="mt-2">
                <div className="fw-bold">Existing Report(s) for this Project:</div>
                {reports.length ? (
                    <ul className="mb-0">
                        {reports.map(report => (
                            <li key={report.reportId} className="mt-3">
                                <span>{report.reportTech}</span>
                                <span className="ms-2">
                                    {report.startDate} ~ {report.endDate}
                                </span>
                                {report.pdfId ? (
                                    <a
                                        className="ms-2 btn btn-primary"
                                        href={`https://drive.google.com/file/d/${report.pdfId}/view?usp=drivesdk`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View
                                    </a>
                                ) : null}
                                <span
                                    className="ms-2"
                                    style={{ color: simpleStatusColorCode[report.status as keyof typeof simpleStatusColorCode] }}
                                >
                                    {report.status}
                                </span>
                                {report.status === "Created" ? (
                                    <button
                                        type="button"
                                        className="ms-2 btn btn-danger"
                                        onClick={async () => {
                                            if (!window.confirm("Are you sure to delete this report shell?")) return;
                                            const formData = new FormData();
                                            formData.append("reportId", report.reportId);
                                            formData.append("id", project.id);
                                            const res = await myAxios({
                                                fileName: "projects/deleteNewReport.php",
                                                formData,
                                            });
                                            if (!res) return;
                                            setReports(Array.isArray(res.data) ? res.data : []);
                                        }}
                                    >
                                        Delete
                                    </button>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="mt-2">No existing report.</div>
                )}
                <div className="border rounded d-flex align-items-center p-2 mt-2">
                    <label htmlFor="newReportTechId" className="text-nowrap required-field fw-bold">
                        New Report Tech:
                    </label>
                    <AsyncFormSelect
                        className = "ms-2 flex-grow-1"
                        placeholder = "Search user..."
                        required = {true}
                        name = "reportTechId"
                        inputId = "reportTechId"
                        fileName = "common/searchUser.php"
                        value = {formValues.reportTechId ? { label: formValues.reportTechName, value: formValues.reportTechId } : null}
                        onChange={next =>
                            setFormValues(prev => ({
                                ...prev,
                                reportTechId: (next as SelectOption)?.value ?? "",
                                reportTechName: (next as SelectOption)?.label ?? "",
                            }))
                        }
                    />
                    <button
                        type="button"
                        className="ms-3 btn btn-primary"
                        onClick={async () => {
                            if (!newReportTechId) {
                                window.alert("Select Report Tech First.");
                                return;
                            }
                            if (!window.confirm("Are you sure to add an empty report shell for this project?")) return;
                            const formData = new FormData();
                            formData.append("id", project.id);
                            formData.append("reportTechId", newReportTechId);
                            const res = await myAxios({
                                fileName: "projects/addNewReport.php",
                                formData,
                            });
                            if (!res) return;
                            setReports(Array.isArray(res.data) ? res.data : []);
                        }}
                    >
                        Add
                    </button>
                </div>
            </div>
        )}
    </>
) : null}
*/
